import * as React from 'react';
import { format, type Locale } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { formatDateVi, formatYmd, parseYmd } from '../lib/dateStrings';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export type DatePickerProps = {
  /** 'YYYY-MM-DD' — same contract as `<input type="date">`. */
  value: string | null | undefined;
  /** '' is never emitted here; clearing is the caller's business via `clearable`. */
  onChange: (value: string) => void;
  /** 'YYYY-MM-DD' inclusive bounds. */
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  className?: string;
  locale: Locale;
  weekStartsOn?: 0 | 1;
  /** 'dropdown' for birthdays (year/month selects) — pair with `fromYear`/`toYear`. */
  captionLayout?: 'label' | 'dropdown';
  fromYear?: number;
  toYear?: number;
  /** Shows a clear action in the popover footer that calls `onChange('')`. */
  clearable?: boolean;
  clearLabel?: string;
  formatValue?: (d: Date) => string;
};

/** Vietnamese-locale date picker with the same string contract as a native
 *  `<input type="date">`. See `apps/dashboard/src/components/pickers/DatePicker`
 *  for the vi-bound wrapper call sites should use. */
export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select date',
  disabled,
  id,
  'aria-label': ariaLabel,
  className,
  locale,
  weekStartsOn,
  captionLayout = 'label',
  fromYear,
  toYear,
  clearable,
  clearLabel = 'Clear',
  formatValue,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = parseYmd(value);

  const disabledMatchers = React.useMemo(() => {
    const minDate = parseYmd(min);
    const maxDate = parseYmd(max);
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (minDate) matchers.push({ before: minDate });
    if (maxDate) matchers.push({ after: maxDate });
    return matchers;
  }, [min, max]);

  const display = selected ? (formatValue ? formatValue(selected) : formatDateVi(value)) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-[28.8px] w-auto justify-start gap-2 text-left font-normal',
            !display && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0" />
          {display ?? placeholder}
        </Button>
      </PopoverTrigger>
      {/* w-auto, not a fixed max-w: the calendar is wider than 300px and a cap
          clips its right columns. Radix already portals this content, so it
          sizes to the calendar (see InvoiceFormPage's prior inline version). */}
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected ?? undefined}
          onSelect={(date) => {
            if (date) {
              onChange(formatYmd(date));
              setOpen(false);
            }
          }}
          disabled={disabledMatchers}
          locale={locale}
          weekStartsOn={weekStartsOn}
          captionLayout={captionLayout}
          startMonth={fromYear !== undefined ? new Date(fromYear, 0, 1) : undefined}
          endMonth={toYear !== undefined ? new Date(toYear, 11, 31) : undefined}
          className="p-4"
          formatters={{
            // The shared Calendar hardcodes an Intl 'default'-locale month dropdown;
            // use the passed locale so the birthday picker's month select is Vietnamese.
            formatMonthDropdown: (date) => format(date, 'LLLL', { locale }),
            formatWeekdayName: (date) => {
              const day = date.getDay();
              return ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][day]!;
            },
          }}
          autoFocus
        />
        {clearable ? (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              {clearLabel}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
